/**
 * A dimension's own value tree, flattened into a searchable list: shared by the move dialog, the
 * merge dialog (`ValueTree.tsx`) and a proposal's "Dentro de"/"Mesclar em" pickers
 * (`ProposalsQueue.tsx`). Each option shows its ancestor path the way `ParentPicker`
 * (`../../campaign/teams/ParentPicker.tsx`) does, so two values named the same are still told
 * apart.
 */
import type { ReactNode } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { om } from './messages';
import type { OntologyValue } from './api/schemas';

function ancestorNames(value: OntologyValue, byId: Map<string, OntologyValue>): string[] {
  const names: string[] = [];
  let current = value.parent_id !== null ? byId.get(value.parent_id) : undefined;
  while (current !== undefined) {
    names.unshift(current.name);
    current = current.parent_id !== null ? byId.get(current.parent_id) : undefined;
  }
  return names;
}

function pathOf(value: OntologyValue, byId: Map<string, OntologyValue>): string {
  return [...ancestorNames(value, byId), value.name].join(' › ');
}

export function ValuePicker(props: {
  values: OntologyValue[];
  /** Ids the picker must not offer: the value itself, its whole subtree (a move target,
   *  which never accepts one of its own descendants), or nothing at all (a merge target,
   *  which only excludes the value itself -- `ValueTree.tsx` builds each set). */
  exclude: ReadonlySet<string>;
  /** Offers "Nível superior" as a top-of-list choice; the merge picker omits it -- merging
   *  into "no value" is not a thing this API models. */
  allowTopLevel?: boolean;
  onSelect: (valueId: string | null) => void;
}): ReactNode {
  const { values, exclude, allowTopLevel = false, onSelect } = props;
  const byId = new Map(values.map((value) => [value.id, value]));
  const options = values
    .filter((value) => !exclude.has(value.id))
    .map((value) => ({ value, path: pathOf(value, byId) }))
    .sort((a, b) => a.path.localeCompare(b.path, 'pt-BR'));

  return (
    <Command>
      <CommandInput placeholder={om('values.picker.searchPlaceholder')} />
      <CommandList>
        <CommandEmpty>{om('values.picker.empty')}</CommandEmpty>
        <CommandGroup>
          {allowTopLevel && (
            <CommandItem value={om('values.move.top')} onSelect={() => onSelect(null)}>
              {om('values.move.top')}
            </CommandItem>
          )}
          {options.map(({ value, path }) => (
            <CommandItem key={value.id} value={path} onSelect={() => onSelect(value.id)}>
              {path}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
